import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { useTheme } from 'hooks/useTheme';
import useMediaQuery from 'hooks/useMediaQuery';
import { renderWithLineBreaks } from 'utils/renderWithLineBreaks';

gsap.registerPlugin(ScrollTrigger);

const Client = ({ clientData }) => {
  const { isDarkMode } = useTheme();
  const isMobileSize = useMediaQuery();
  const clientContainerRef = useRef(null);
  const imageStackRef = useRef(null);
  const imageRefs = useRef([]);
  const stepItemRefs = useRef([]);
  const masterTl = useRef(null);

  const formatNumber = (num) => {
    return num >= 1 && num <= 9 ? `0${num}` : `${num}`;
  };

  useGSAP(
    () => {
      if (
        !clientContainerRef.current ||
        !imageStackRef.current ||
        !clientData.images ||
        clientData.images.length === 0 ||
        imageRefs.current.length === 0 ||
        stepItemRefs.current.length === 0
      ) {
        return;
      }

      const isTouchDevice = 'ontouchstart' in window;

      const pinType = isTouchDevice ? 'fixed' : 'transform';

      ScrollTrigger.getById('clientPin')?.kill();
      ScrollTrigger.getById('clientScrub')?.kill();
      masterTl.current?.kill();
      masterTl.current = gsap.timeline();

      gsap.set([imageRefs.current, stepItemRefs.current], {
        clearProps: 'all',
      });

      imageRefs.current = imageRefs.current.slice(0, clientData.images.length);
      stepItemRefs.current = stepItemRefs.current.slice(
        0,
        clientData.images.length,
      );

      gsap.set(imageRefs.current, {
        zIndex: 1,
        clipPath: 'polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)',
      });
      gsap.set(imageRefs.current[0], {
        zIndex: 2,
        clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
      });

      const ACTIVE_OPACITY = 1;
      const INACTIVE_OPACITY = 0.4;

      stepItemRefs.current.forEach((item, index) => {
        if (item) {
          gsap.set(item, {
            opacity: index === 0 ? ACTIVE_OPACITY : INACTIVE_OPACITY,
          });
          item.classList.toggle('active-step', index === 0);
        }
      });

      const scrollDistancePerImage = 600;
      const totalScrollDuration =
        (clientData.images.length - 1) * scrollDistancePerImage;

      ScrollTrigger.create({
        id: 'clientPin',
        trigger: clientContainerRef.current,
        start: 'top top',
        end: `+=${totalScrollDuration}`,
        pin: true,
        pinType: pinType,
        anticipatePin: 0.01,
      });

      masterTl.current = gsap.timeline({
        scrollTrigger: {
          id: 'clientScrub',
          trigger: clientContainerRef.current,
          start: 'top top',
          end: `+=${totalScrollDuration}`,
          scrub: true,
          onUpdate: (self) => {
            const totalSteps = clientData.images.length - 1;
            const progress = self.progress;
            const activeIndex = Math.round(progress * totalSteps);

            stepItemRefs.current.forEach((item, index) => {
              if (item) {
                item.classList.toggle('active-step', index === activeIndex);
              }
            });
          },
        },
      });

      for (let i = 1; i < clientData.images.length; i++) {
        const prevImage = imageRefs.current[i - 1];
        const currentImage = imageRefs.current[i];
        const prevStep = stepItemRefs.current[i - 1];
        const currentStep = stepItemRefs.current[i];
        const label = `image-transition-${i - 1}`;

        masterTl.current
          .to(
            currentImage,
            {
              clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
              duration: 1,
              ease: 'none',
              overwrite: 'auto',
              onStart: () => {
                gsap.set(currentImage, { zIndex: 2 });
              },
            },
            label,
          )
          .to(
            prevImage,
            {
              clipPath: 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)',
              duration: 1,
              ease: 'none',
              overwrite: 'auto',
              onComplete: () => {
                gsap.set(prevImage, { zIndex: 1 });
              },
            },
            `${label}+=0.5`,
          )
          .to(
            prevStep,
            {
              opacity: INACTIVE_OPACITY,
              duration: 0.5,
              ease: 'none',
              overwrite: 'auto',
            },
            label,
          )
          .to(
            currentStep,
            {
              opacity: ACTIVE_OPACITY,
              duration: 0.5,
              ease: 'none',
              overwrite: 'auto',
            },
            label,
          )
          .addLabel(
            `end-transition-${i - 1}`,
            `+=${
              scrollDistancePerImage / (clientData.images.length - 1) / 1000
            }`,
          );
      }

      return () => {
        ScrollTrigger.getById('clientPin')?.kill();
        ScrollTrigger.getById('clientScrub')?.kill();
        masterTl.current?.kill();
      };
    },
    {
      scope: clientContainerRef,
      dependencies: [clientData.images, isDarkMode],
    },
  );

  return (
    <div
      className={`client-container page-wrapper full-pager flex flex-column justify-between ${
        !isMobileSize ? 'client-container-desktop' : ''
      }`}
      ref={clientContainerRef}
    >
      <div className='flex full-width justify-between client-header'>
        {isMobileSize && (
          <div className='flex items-end'>
            <p className='uppercase client-header-title'>
              {renderWithLineBreaks(clientData.showRoomTitle)}
            </p>
          </div>
        )}
        <span>{clientData.year}</span>
        <div className='client-steps flex flex-column'>
          {clientData.images?.length > 0 &&
            clientData.images.map((item, index) => (
              <div
                key={item.name}
                className={`flex client-step-item column-gap-24`}
                ref={(el) => (stepItemRefs.current[index] = el)}
              >
                {!isMobileSize && (
                  <span className='index-step'>{formatNumber(index + 1)}</span>
                )}
                <span>{item.name}</span>
              </div>
            ))}
        </div>
        {!isMobileSize && clientData.tags?.length > 0 && (
          <div className='client-tags flex flex-column'>
            {clientData.tags.map((tag, index) => (
              <span key={tag + index}>{tag}</span>
            ))}
          </div>
        )}
      </div>
      <div className='client-image-container flex full-width justify-between'>
        {!isMobileSize && (
          <div className='flex items-end'>
            <p className='uppercase client-header-title'>
              {renderWithLineBreaks(clientData.showRoomTitle)}
            </p>
          </div>
        )}
        <div
          className='client-image-stack relative full-height'
          ref={imageStackRef}
        >
          {clientData.images?.length &&
            clientData.images.map((item, index) => (
              <img
                key={item.name + index}
                src={
                  isDarkMode
                    ? item.darkModeImageUrl || item.lightModeImageUrl
                    : item.lightModeImageUrl
                }
                alt={item.name}
                className='full-height full-width absolute'
                ref={(el) => (imageRefs.current[index] = el)}
              />
            ))}
        </div>
        {isMobileSize && clientData.tags?.length > 0 && (
          <div className='client-tags'>
            {clientData.tags.map((tag, index) => (
              <span key={tag + index}>
                {tag + (index < clientData.tags.length - 1 ? ', ' : '')}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Client;
